import { Outlet, useNavigate } from "react-router";
import { useAppNavigate } from "../hooks/useAppNavigate";
import { useCallback, Suspense, useRef, useState, useLayoutEffect, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAppSelector } from "../redux/hooks";
import { toggleCart } from "../redux/slices/cartSlice";
import { updateWidgetData, addWidgetSection, updateSiteContentRequest, selectPlainSiteContent } from "../redux/slices/siteContentSlice";
import { toggleNavEditMode } from "../redux/slices/navEditModeSlice";
import { toggleFooterEditMode } from "../redux/slices/footerEditModeSlice";
import RestaurantGate from "../components/RestaurantGate";
import Cart from "../components/Cart";
import widgetRegistry from "../widgets/widgetRegistry";
import { DEFAULT_NAV_RAW_DATA, DEFAULT_ESSENTIAL_FOOTER_DATA } from "../data/defaultWidgets";
import type { NavItem } from "../components/navigation";
import type { RootState } from "../redux/store";
import type { ClassicNavData } from "../widgets/nav/ClassicNav";
import type { EssentialFooterData } from "../widgets/footer/EssentialFooter";

interface ProtectedLayoutProps {
  className?: string;
  requiresRestaurant?: boolean;
  redirectTo?: string;
}

export default function ProtectedLayout({
  className = "",
  requiresRestaurant = true,
  redirectTo = "/select-location"
}: ProtectedLayoutProps = {}) {
  const navigate = useNavigate();
  const appNavigate = useAppNavigate();
  const dispatch = useDispatch();
  const siteContent = useSelector(selectPlainSiteContent);
  const { fetchLoading, updateLoading } = useAppSelector((state) => state?.siteContent);
  const { isEditMode } = useAppSelector((state) => state.editMode);
  const { isEditControl } = useAppSelector((state) => state.editControl);
  const isNavEditMode = useSelector((state: RootState) => state.navEditMode.isNavEditMode);
  const isFooterEditMode = useSelector((state: RootState) => state.footerEditMode.isFooterEditMode);

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const isSavingNav = useRef(false);
  const isSavingFooter = useRef(false);

  // Extract plain widget arrays to prevent proxy access (same pattern as HomePage)
  const originalWidgets = siteContent?.widgets ?? [];

  // Local state for nav and footer data (same pattern as usePageWidget)
  const [localNavData, setLocalNavData] = useState<ClassicNavData | null>(null);
  const [localFooterData, setLocalFooterData] = useState<EssentialFooterData | null>(null);

  /**
   * Auto-mount navigation widget if none exists
   */
  useEffect(() => {
    if (!originalWidgets.length) return;
    
    const hasNavWidget = originalWidgets.some((widget: any) => 
      widget?.type?.toLowerCase().includes("nav")
    );
    
    if (!hasNavWidget) {
      dispatch(addWidgetSection({
        widget: { type: "ClassicNav", data: DEFAULT_NAV_RAW_DATA },
      }));
      dispatch(updateSiteContentRequest());
    }
  }, [originalWidgets, dispatch]);

  /**
   * Auto-mount footer widget if none exists
   */
  useEffect(() => {
    if (!originalWidgets.length) return;
    
    const hasFooterWidget = originalWidgets.some((widget: any) => 
      widget?.type?.toLowerCase().includes("footer")
    );
    
    if (!hasFooterWidget) {
      dispatch(addWidgetSection({
        widget: { type: "EssentialFooter", data: DEFAULT_ESSENTIAL_FOOTER_DATA },
      }));
      dispatch(updateSiteContentRequest());
    }
  }, [originalWidgets, dispatch]);

  /**
   * Auto-exit navigation edit mode after save completes
   */
  useEffect(() => {
    if (isSavingNav.current && !updateLoading && !fetchLoading) {
      dispatch(toggleNavEditMode());
      isSavingNav.current = false;
    }
  }, [updateLoading, fetchLoading, dispatch]);

  /**
   * Auto-exit footer edit mode after save completes
   */
  useEffect(() => {
    if (isSavingFooter.current && !updateLoading && !fetchLoading) {
      dispatch(toggleFooterEditMode());
      isSavingFooter.current = false;
    }
  }, [updateLoading, fetchLoading, dispatch]);

  /**
   * Navigation save handler - Save local data to Redux then backend
   */
  const handleNavSave = useCallback(() => {
    if (!updateLoading && localNavData) {
      const headerWidgetIndex = originalWidgets.findIndex((w: any) => 
        w?.type?.toLowerCase().includes("nav")
      );
      
      if (headerWidgetIndex !== -1) {
        // Widget exists - update it
        dispatch(updateWidgetData({ 
          index: headerWidgetIndex, 
          data: localNavData
        }));
      } else {
        // Widget doesn't exist - add it
        dispatch(addWidgetSection({
          widget: { type: "ClassicNav", data: localNavData },
          afterIndex: -1 // Add at the beginning
        }));
      }
      
      isSavingNav.current = true;
      dispatch(updateSiteContentRequest());
    }
  }, [dispatch, updateLoading, localNavData, originalWidgets]);

  /**
   * Footer save handler - Save local data to Redux then backend
   */
  const handleFooterSave = useCallback(() => {
    if (!updateLoading && localFooterData) {
      const footerWidgetIndex = originalWidgets.findIndex((w: any) => 
        w?.type?.toLowerCase().includes("footer")
      );
      
      if (footerWidgetIndex !== -1) {
        dispatch(updateWidgetData({ 
          index: footerWidgetIndex, 
          data: localFooterData
        }));
      }
      
      isSavingFooter.current = true;
      dispatch(updateSiteContentRequest());
    }
  }, [dispatch, updateLoading, localFooterData, originalWidgets]);

  // Extract header widget and data directly (using plain array to prevent proxy access)
  const { HeaderComponent, headerData } = useMemo(() => {
    const header = originalWidgets.find((w: any) => w?.type?.toLowerCase().includes("nav"));

    // If no nav widget exists, use default data with ClassicNav
    const navData = header?.data || DEFAULT_NAV_RAW_DATA;
    const navType = header?.type || "ClassicNav";
    
    const HeaderComponent = widgetRegistry[navType as keyof typeof widgetRegistry] || null;

    // Use local data when in edit mode, otherwise use Redux data
    const displayData = isNavEditMode && localNavData ? localNavData : navData;

    return { HeaderComponent, headerData: displayData as ClassicNavData };
  }, [originalWidgets, isNavEditMode, localNavData]);

  // Extract footer widget and data directly (using plain array to prevent proxy access)
  const { FooterComponent, footerData } = useMemo(() => {
    const footer = originalWidgets.find((w: any) => w?.type?.toLowerCase().includes("footer"));

    // If no footer widget exists, use default data with EssentialFooter
    const footerWidgetData = footer?.data || DEFAULT_ESSENTIAL_FOOTER_DATA;
    const footerType = footer?.type || "EssentialFooter";
    
    const FooterComponent = widgetRegistry[footerType as keyof typeof widgetRegistry] || null;

    // Use local data when in edit mode, otherwise use Redux data
    const displayData = isFooterEditMode && localFooterData ? localFooterData : footerWidgetData;

    return { FooterComponent, footerData: displayData as EssentialFooterData };
  }, [originalWidgets, isFooterEditMode, localFooterData]);

  // Sync local nav data when Redux data changes or edit mode starts
  useEffect(() => {
    const header = originalWidgets.find((w: any) => w?.type?.toLowerCase().includes("nav"));
    const navData = header?.data || DEFAULT_NAV_RAW_DATA;
    
    if (isNavEditMode && !localNavData) {
      // Data is already clean from selectPlainSiteContent, no need for proxy unwrapping
      setLocalNavData(navData);
    } else if (!isNavEditMode) {
      // Clear local data when exiting edit mode
      setLocalNavData(null);
    }
  }, [originalWidgets, isNavEditMode, localNavData]);

  // Sync local footer data when Redux data changes or edit mode starts
  useEffect(() => {
    const footer = originalWidgets.find((w: any) => w?.type?.toLowerCase().includes("footer"));
    const footerWidgetData = footer?.data || DEFAULT_ESSENTIAL_FOOTER_DATA;
    
    if (isFooterEditMode && !localFooterData) {
      // Data is already clean from selectPlainSiteContent, no need for proxy unwrapping
      setLocalFooterData(footerWidgetData);
    } else if (!isFooterEditMode) {
      // Clear local data when exiting edit mode
      setLocalFooterData(null);
    }
  }, [originalWidgets, isFooterEditMode, localFooterData]);

  /**
   * Track header height dynamically (resizes + widget changes)
   */
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) resizeObserver.observe(headerRef.current);

    return () => resizeObserver.disconnect();
  }, [originalWidgets]);


  /**
   * Handlers
   */
  const handleNavClick = useCallback(
    (item: NavItem) => {
      if (typeof item.url === "string") {
        appNavigate(item.url);
      }
    },
    [appNavigate]
  );

  const handleLogoClick = useCallback(() => appNavigate("/"), [appNavigate]);
  const handleCartClick = useCallback(() => {
    dispatch(toggleCart());
  }, [dispatch]);

  const handleUpdate = useCallback(
    (newData: any) => {
      if (isNavEditMode) {
        // Data is already clean, no need for proxy unwrapping
        setLocalNavData(newData);
      }
    },
    [isNavEditMode]
  );

  const handleFooterUpdate = useCallback(
    (newData: any) => {
      if (isFooterEditMode) {
        // Data is already clean, no need for proxy unwrapping
        setLocalFooterData(newData);
      }
    },
    [isFooterEditMode]
  );

  /**
   * Navigation edit mode toggle handler
   */
  const handleNavEditModeToggle = useCallback(() => {
    if (isNavEditMode) {
      handleNavSave();
    } else {
      dispatch(toggleNavEditMode());
    }
  }, [isNavEditMode, handleNavSave, dispatch]);

  /**
   * Footer edit mode toggle handler
   */
  const handleFooterEditModeToggle = useCallback(() => {
    if (isFooterEditMode) {
      handleFooterSave();
    } else {
      dispatch(toggleFooterEditMode());
    }
  }, [isFooterEditMode, handleFooterSave, dispatch]);

  return (
    <div className={className}>
      {/* Navigation Edit/Save Icon - Only show when edit control is enabled AND main page is not in edit mode */}
      {isEditControl && !isEditMode && (
        <button
          onClick={handleNavEditModeToggle}
          disabled={updateLoading || fetchLoading}
          className="fixed left-1/2 transform -translate-x-1/2 z-[9999] px-4 py-2 rounded-full transition-colors flex items-center space-x-2"
          style={{ 
            backgroundColor: '#EE4445', 
            color: 'white',
            top: `${headerHeight - 18}px` // Position higher up with more overlap
          }}
          title={isNavEditMode ? "Save navigation changes" : "Edit navigation"}
        >
          {isNavEditMode ? (
            <>
              {/* Save Icon (Floppy Disk) */}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              <span className="text-sm font-medium">Save</span>
            </>
          ) : (
            <>
              {/* Edit Icon (Clean Pencil) */}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="text-sm font-medium">Edit</span>
            </>
          )}
        </button>
      )}

      <header
        ref={headerRef}
        className={`left-0 right-0 z-50 ${isNavEditMode ? 'relative' : 'fixed top-0'}`}
      >
        {HeaderComponent && (
          <Suspense
            fallback={<div className="h-16 bg-white shadow-md animate-pulse" />}
          >
            <HeaderComponent
              data={headerData as any}
              onNavClick={handleNavClick}
              onLogoClick={handleLogoClick}
              onCartClick={handleCartClick}
              onUpdate={handleUpdate}
              onSave={handleNavSave}
              isEditing={isNavEditMode}
              className="!sticky !top-0"
            />
          </Suspense>
        )}
      </header>

      <main 
        className="min-h-screen"
        style={{ 
          marginTop: isNavEditMode ? 0 : headerHeight
        }}
      >
        <RestaurantGate
          requiresRestaurant={requiresRestaurant}
          redirectTo={redirectTo}
        >
          <Outlet />
        </RestaurantGate>

        <footer className="w-full relative">
          {/* Footer Edit/Save Icon - Only show when edit control is enabled AND main page is not in edit mode */}
          {isEditControl && !isEditMode && (
            <button
              onClick={handleFooterEditModeToggle}
              disabled={updateLoading || fetchLoading}
              className="absolute left-1/2 transform -translate-x-1/2 -top-3 z-10 px-4 py-2 rounded-full transition-colors flex items-center space-x-2"
              style={{ backgroundColor: '#EE4445', color: 'white' }}
              title={isFooterEditMode ? "Save footer changes" : "Edit footer"}
            >
              {isFooterEditMode ? (
                <>
                  {/* Save Icon (Floppy Disk) */}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  <span className="text-sm font-medium">Save</span>
                </>
              ) : (
                <>
                  {/* Edit Icon (Clean Pencil) */}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <span className="text-sm font-medium">Edit</span>
                </>
              )}
            </button>
          )}

          {FooterComponent && (
            <Suspense
              fallback={<div className="h-32 bg-gray-800 animate-pulse" />}
            >
              <FooterComponent
                data={footerData as any}
                onUpdate={handleFooterUpdate}
                onSave={handleFooterSave}
                isEditing={isFooterEditMode}
              />
            </Suspense>
          )}
        </footer>
      </main>

      <Cart />
    </div>
  );
}
